import { collection, doc, getDoc, updateDoc } from 'firebase/firestore';

import { getFirestoreInstance } from './index';

export interface LKDesktopNotificationSettings {
  notificationDate: Date;
  sunsetDate: Date;
  version: string;
}

export const getLKDesktopNotificationSettings = async (): Promise<LKDesktopNotificationSettings | null> => {
  const db = await getFirestoreInstance();
  const docSnapshot = await getDoc(doc(db, 'live-settings/lk-desktop-notification'));

  if (!docSnapshot.exists()) return null;

  const data = docSnapshot.data();
  const notificationDate = data.notification_date?.toDate?.();
  const sunsetDate = data.sunset_date?.toDate?.();

  if (!(notificationDate instanceof Date) || !(sunsetDate instanceof Date) || typeof data.version !== 'string') {
    throw new Error('Invalid live-settings/lk-desktop-notification document');
  }

  return { notificationDate, sunsetDate, version: data.version };
};

export const getExistingFlags = async (): Promise<string[]> => {
  const db = await getFirestoreInstance();

  // Retrieve the "feature-flags" document inside the "live-settings" collection
  const docSnapshot = await getDoc(doc(db, 'live-settings/feature-flags'));

  // Check if the document exists
  if (!docSnapshot.exists) {
    return [];
  }

  // Extract all flag keys from the document fields
  const flags = docSnapshot.data();
  return flags ? Object.keys(flags) : [];
};

export const updateFlagFromAvaId = async (avaId: string, flags: Record<string, boolean>) => {
  try {
    const existingFlags = await getExistingFlags();

    // Check if the provided flags are valid
    for (const flag in flags) {
      if (!existingFlags.includes(flag)) {
        throw new Error(`Invalid flag provided: ${flag}`);
      }
    }

    const db = await getFirestoreInstance();

    // Get the document reference for the user
    const userDocRef = doc(collection(db, 'users'), avaId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists) {
      console.error('No document found for this user');
      return null;
    }

    // Get the existing user data
    const userData = userDoc.data();
    if (!userData) {
      console.error('No data found for this user');
      return null;
    }

    // Initialize the flags object if it doesn't exist
    const existingFlagsObj = userData.flags || {};

    // Update the flags with the provided key-value pairs
    const updatedFlags = { ...existingFlagsObj };

    for (const [key, value] of Object.entries(flags)) {
      updatedFlags[key] = value; // Add or update the flag
    }

    // Update the document with the modified flags
    await updateDoc(userDocRef, {
      flags: updatedFlags,
    });

    return updatedFlags;
  } catch (error) {
    console.error('Error updating user features: ', error);
  }
};

export const toggleSpeakerId = async (avaId: string, flagsState: boolean) => {
  await updateFlagFromAvaId(avaId, { ['mono-segmentation']: flagsState, nats: flagsState });
};
